import { FuuzeButton, FuuzeText } from '@fuuze/shared';
import { FuuzeColors, mediaQuery } from '@fuuze/shared';
import type { ReactNode } from 'react';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { IoIosCheckmarkCircleOutline } from 'react-icons/io';
import { IoClose, IoWarningOutline } from 'react-icons/io5';
import { RxCrossCircled } from 'react-icons/rx';
import { ToastContainer, type ToastContentProps, type ToastOptions, toast } from 'react-toastify';
import styled, { css } from 'styled-components';

export namespace Toast {
  type ContentProps = { title: ReactNode; content?: string; actions?: ReactNode };

  export function showToast(options: ToastOptions<ContentProps>) {
    toast(ToastComponent, {
      ...options,
      position: options?.position || 'bottom-right',
      autoClose: options?.autoClose !== undefined ? options.autoClose : 5000,
      closeButton: false,
      icon: false,
      theme: 'dark',
      type: options.type || 'info',
      style: {
        backgroundColor: FuuzeColors.background,
        ...options?.style,
      },
    });
  }

  export const FuuzeToastContainer = styled(ToastContainer)<{ playerHeight: number }>`
    bottom: calc(${props => props.playerHeight}px + ${props => props.theme.variables.navigationBarHeight});
    padding: 0;

    @media ${mediaQuery.desktop} {
      bottom: ${props => props.playerHeight}px;
    }

    .Toastify__toast {
      padding: 0;
      background-color: ${props => props.theme.colors.background};
      width: 95%;
      align-self: center;
      max-width: 600px;
      align-items: normal;

      @media ${mediaQuery.mobile} {
        width: auto;
      }

      --toastify-color-progress-info: #3f5ee3;
      --toastify-color-progress-success: #6fc469;
      --toastify-color-progress-warning: #deb32d;
      --toastify-color-progress-error: #e41a0c;
      --toastify-color-progress-dark: ${props => props.theme.colors.orange.orange100};
    }
  `;

  function ToastComponent(props: ToastContentProps<ContentProps>) {
    const Icon = () => {
      switch (props.toastProps.type) {
        case 'info':
          return <AiOutlineInfoCircle size={24} className='icon' />;
        case 'success':
          return <IoIosCheckmarkCircleOutline size={24} className='icon' />;
        case 'error':
          return <RxCrossCircled size={24} className='icon' />;
        case 'warning':
          return <IoWarningOutline size={24} className='icon' />;
        default:
          return null;
      }
    };

    return (
      <Wrapper $type={props.toastProps.type}>
        <Icon />
        <FuuzeText as='span' className='title'>
          {props.data.title}
        </FuuzeText>
        <FuuzeButton icon className='close'>
          <IoClose />
        </FuuzeButton>
        {props.data.content && <FuuzeText className='content'>{props.data.content}</FuuzeText>}
        <div className='actions'>{props.data.actions}</div>
      </Wrapper>
    );
  }

  const Wrapper = styled.div<{ $type?: ToastOptions['type'] }>`
    display: grid;
    flex: 1;
    grid-template-columns: ${props => (!props.$type ? '1fr 32px' : '32px 1fr 32px')};
    grid-column-gap: 8px;
    grid-template-areas: ${props =>
      !props.$type
        ? css`"title close" "content content" "action action"`
        : css`"icon title close" ". content content" "action action action"`};
    padding: 8px;
    align-items: center;
    border: 1px solid ${props => props.theme.colors.dark.dark200};
    border-radius: 8px;

    background: ${props => {
      switch (props.$type) {
        case 'info':
          return 'linear-gradient(145deg, rgba(17,19,32,1) 0%, rgba(0,0,0,0) 30%)';
        case 'success':
          return 'linear-gradient(145deg, rgba(16,29,22,1) 0%, rgba(0,0,0,0) 30%)';
        case 'error':
          return 'linear-gradient(145deg, rgba(33,19,19,1) 0%, rgba(0,0,0,0) 30%)';
        case 'warning':
          return 'linear-gradient(145deg, rgba(33,28,11,1) 0%, rgba(0,0,0,0) 30%)';
        default:
          return 'transparent';
      }
    }};

    .icon {
      grid-area: icon;
      place-self: center;
      display: ${props => (props.$type ? 'block' : 'none')};
      filter: ${props => {
        switch (props.$type) {
          case 'info':
            return 'brightness(0) saturate(100%) invert(46%) sepia(64%) saturate(6861%) hue-rotate(222deg) brightness(92%) contrast(93%)';
          case 'success':
            return 'brightness(0) saturate(100%) invert(66%) sepia(46%) saturate(424%) hue-rotate(68deg) brightness(94%) contrast(94%)';
          case 'error':
            return 'brightness(0) saturate(100%) invert(18%) sepia(82%) saturate(4027%) hue-rotate(355deg) brightness(89%) contrast(102%)';
          case 'warning':
            return 'brightness(0) saturate(100%) invert(84%) sepia(13%) saturate(2344%) hue-rotate(351deg) brightness(89%) contrast(95%)';
          default:
            return 'none';
        }
      }};
    }

    .title {
      grid-area: title;
      font-size: 0.85rem;

      @media ${mediaQuery.mobile} {
        font-size: 0.9rem;
      }
    }

    .close {
      grid-area: close;
      place-self: center;
      padding-right: 0;
    }

    .content {
      grid-area: content;
      color: ${props => props.theme.colors.dark.dark700};
      font-size: 0.75rem;
      margin-top: 8px;
      margin-bottom: 16px;

      @media ${mediaQuery.mobile} {
        font-size: 0.8rem;
      }
    }

    .actions {
      grid-area: action;
      place-self: end;
    }
  `;
}
